"use client";

import { useMemo } from "react";
import Image from "next/image";
import { useTripData } from "@/lib/trip-data-context";
import { Card, TabLoading, EmptyState } from "@/components/trip/ui";
import type { TimelineItem } from "@/lib/types";

export default function HighlightsPage() {
  const { data, loading } = useTripData();

  // Lookup: dayId → { timelineItemId → TimelineItem }
  const dayMap = useMemo(() => {
    const map = new Map<string, Map<string, TimelineItem>>();
    for (const day of data?.days ?? []) {
      map.set(day.id, new Map(day.timeline.map((item) => [item.id, item])));
    }
    return map;
  }, [data]);

  if (loading || !data) {
    return <TabLoading />;
  }

  if (data.highlights.length === 0) {
    return <EmptyState>Highlights coming soon — still being planned.</EmptyState>;
  }

  return (
    <div>
      <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, margin: "0 0 24px" }}>
        The headliners — one card per city.
      </p>
      {data.highlights.map((h) => (
        <Card key={h.city} style={{ padding: 0, overflow: "hidden" }}>
          {h.image && (
            <div style={{ position: "relative", width: "100%", height: 180 }}>
              <Image src={h.image.src} alt={h.image.alt} fill style={{ objectFit: "cover" }} unoptimized />
            </div>
          )}
          <div style={{ padding: "16px 18px" }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{h.city}</div>
            {h.items.map((ref, i) => {
              const timelineItem = dayMap.get(ref.dayId)?.get(ref.timelineItemId);
              const label = ref.label ?? timelineItem?.title ?? ref.timelineItemId;
              return (
                <div key={i} style={{ fontSize: 14, color: "var(--text2)", padding: "3px 0", lineHeight: 1.5 }}>
                  ★ {label}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
